import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Search, Eye, Edit, Trash2, Building2, Mail, Phone, MapPin, Car, CheckCircle, XCircle, Clock, Calendar, DollarSign, TrendingUp, Save, X } from 'lucide-react';
import { ROUTES } from '../../constants/routes.constants';
import { useLanguage } from '../../context/LanguageContext';

/**
 * New Agencies List Page
 * Admin can view, edit, and delete all agencies with their details
 */
const AgenciesListNew = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [agencyStats, setAgencyStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
  });

  const loadAgencies = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        console.error('No access token found');
        setIsLoading(false);
        return;
      }

      const params = new URLSearchParams();
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch agencies');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setAgencies(result.data);
      } else {
        setAgencies([]);
      }
    } catch (error) {
      console.error('Error loading agencies:', error);
      alert('Erreur lors du chargement des agences: ' + error.message);
      setAgencies([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadAgencies();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadAgencyStats = useCallback(async (agencyId) => {
    setIsLoadingStats(true);
    setAgencyStats(null);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        console.error('No access token found');
        setIsLoadingStats(false);
        return;
      }

      const response = await fetch(`${ROUTES.API_BASE_URL}/v1/agencies/${agencyId}/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch agency stats');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setAgencyStats(result.data);
      }
    } catch (error) {
      console.error('Error loading agency stats:', error);
      setAgencyStats(null);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  const handleViewDetails = (agency) => {
    // Naviguer vers la page de détails de l'agence
    navigate(ROUTES.ADMIN_AGENCY_DETAILS_BY_ID(agency.id));
  };

  const handleEdit = (agency) => {
    setSelectedAgency(agency);
    setEditFormData({
      nom: agency.nom || '',
      email: agency.email || '',
      telephone: agency.telephone || '',
      adresse: agency.adresse || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedAgency) return;

    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        alert('Token d\'authentification manquant');
        setIsSaving(false);
        return;
      }

      // TODO: Implémenter l'endpoint de mise à jour côté backend
      // Pour l'instant, on simule la sauvegarde
      const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies/${selectedAgency.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la mise à jour');
      }

      alert('Agence mise à jour avec succès');
      setShowEditModal(false);
      loadAgencies();
    } catch (error) {
      console.error('Error updating agency:', error);
      alert('Erreur lors de la mise à jour: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (agency) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'agence "${agency.nom}" ? Cette action est irréversible.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        alert('Token d\'authentification manquant');
        setIsDeleting(false);
        return;
      }

      // TODO: Implémenter l'endpoint de suppression côté backend
      // Pour l'instant, on simule la suppression
      const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies/${agency.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression');
      }

      alert('Agence supprimée avec succès');
      loadAgencies();
    } catch (error) {
      console.error('Error deleting agency:', error);
      alert('Erreur lors de la suppression: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const getAbsoluteUrl = (relativePath) => {
    if (!relativePath || relativePath.startsWith('http')) {
      return relativePath;
    }
    return `${ROUTES.API_BASE_URL.replace('/api', '')}${relativePath}`;
  };

  const getStatusBadge = (statut, vendorStatus) => {
    if (statut === 'active' || vendorStatus === 'APPROVED') {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    } else if (vendorStatus === 'PENDING') {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          En attente
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Inactive
        </span>
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <DashboardLayout>
      <div style={{ paddingTop: '24px', paddingRight: '24px', paddingBottom: '24px', paddingLeft: '0' }}>
        {/* Header */}
        <div className="mb-6" style={{ paddingLeft: '24px' }}>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Liste des agences</h1>
          <p className="text-gray-600">Gérez toutes les agences partenaires de la plateforme</p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" style={{ marginLeft: '24px', marginRight: '0' }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone ou ville..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
            />
          </div>
        </div>

        {/* Table - Collé à la sidebar, pas de padding à gauche */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#27489E]"></div>
              <p className="mt-4 text-gray-600">Chargement des agences...</p>
            </div>
          ) : agencies.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucune agence trouvée</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Agence
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Adresse
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Véhicules
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {agencies.map((agency) => (
                      <tr key={agency.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#27489E] rounded-full flex items-center justify-center text-white font-semibold">
                              {agency.nom.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{agency.nom}</div>
                              <div className="text-sm text-gray-500">{agency.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {agency.telephone && agency.telephone !== 'Non renseigné' ? agency.telephone : agency.email || 'N/A'}
                          </div>
                          {agency.telephone && agency.telephone !== 'Non renseigné' && (
                            <div className="text-sm text-gray-500">
                              {agency.email}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {agency.adresse && agency.adresse !== 'Non renseignée' ? agency.adresse : 'Non renseignée'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {agency.nombreVehicules}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(agency.statut, agency.vendorStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewDetails(agency)}
                              className="p-2 text-[#27489E] hover:bg-[#27489E]/10 rounded-lg transition"
                              title="Voir les détails"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEdit(agency)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                              title="Éditer"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(agency)}
                              disabled={isDeleting}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Supprimer"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Details Modal */}
        {showDetailsModal && selectedAgency && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowDetailsModal(false)}
            ></div>

            <div className="relative bg-white rounded-2xl overflow-hidden w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
              {/* Header */}
              <div className="bg-[#27489E] text-white px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold">Détails de l'agence</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-white hover:text-gray-200 transition"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto flex-1 p-6 space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {/* Carte 1: Ventes Totales */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-600">Ventes Totales</h4>
                      <DollarSign className="w-5 h-5 text-[#27489E]" />
                    </div>
                    {isLoadingStats ? (
                      <div className="flex items-center justify-center h-12">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#27489E]"></div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-2xl font-bold text-[#27489E]">
                          {agencyStats?.totalSales ? agencyStats.totalSales.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">MAD</p>
                      </div>
                    )}
                  </div>

                  {/* Carte 2: Réservations Semaine */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-600">Réservations (Semaine)</h4>
                      <Calendar className="w-5 h-5 text-[#27489E]" />
                    </div>
                    {isLoadingStats ? (
                      <div className="flex items-center justify-center h-12">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#27489E]"></div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {agencyStats?.weeklyBookings ?? 0}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">7 derniers jours</p>
                      </div>
                    )}
                  </div>

                  {/* Carte 3: Gain de la semaine */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-600">Gain de la semaine</h4>
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    {isLoadingStats ? (
                      <div className="flex items-center justify-center h-12">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-2xl font-bold text-green-600">
                          {agencyStats?.weeklyEarnings ? agencyStats.weeklyEarnings.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">MAD</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Agency Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500">Nom de l'agence</label>
                      <p className="text-gray-900 font-medium">{selectedAgency.nom}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <p className="text-gray-900 font-medium">{selectedAgency.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Téléphone</label>
                      <p className="text-gray-900 font-medium">{selectedAgency.telephone}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Statut</label>
                      <div className="mt-1">{getStatusBadge(selectedAgency.statut, selectedAgency.vendorStatus)}</div>
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm text-gray-500">Adresse</label>
                      <p className="text-gray-900 font-medium">{selectedAgency.adresse}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Nombre de véhicules</label>
                      <p className="text-gray-900 font-medium">{selectedAgency.nombreVehicules}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Date de création</label>
                      <p className="text-gray-900 font-medium">{formatDate(selectedAgency.createdAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Logo */}
                {selectedAgency.logo && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Logo</h3>
                    <div className="flex justify-center">
                      <img
                        src={getAbsoluteUrl(selectedAgency.logo)}
                        alt={selectedAgency.nom}
                        className="w-32 h-32 rounded-lg object-cover border border-gray-200"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/128?text=Logo+non+disponible';
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedAgency && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowEditModal(false)}
            ></div>

            <div className="relative bg-white rounded-2xl overflow-hidden w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
              {/* Header */}
              <div className="bg-[#27489E] text-white px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold">Modifier l'agence</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-white hover:text-gray-200 transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto flex-1 p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'agence
                  </label>
                  <input
                    type="text"
                    value={editFormData.nom}
                    onChange={(e) => setEditFormData({ ...editFormData, nom: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={editFormData.telephone}
                    onChange={(e) => setEditFormData({ ...editFormData, telephone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  <textarea
                    value={editFormData.adresse}
                    onChange={(e) => setEditFormData({ ...editFormData, adresse: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="px-4 py-2 bg-[#27489E] text-white rounded-lg hover:bg-[#1e3a7a] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AgenciesListNew;
